// Daily progression snapshots per deck — used to compute 7-day and
// 30-day mastery deltas on the session-end recap. One snapshot per
// local-day per deck, trimmed to PROGRESS_SNAPSHOT_CAP entries.
//
// Kept separate from stats.ts because the shape and lifecycle differ:
// stats is a hot write-path keyed on cardId with Leitner state, while
// this is a write-once-per-day append-only log for trend display.

import { STORAGE_KEYS } from "../config/storage-keys";
import type { CefrLevel } from "../types";

export type ProgressSnapshot = {
  /** YYYY-MM-DD in the user's local timezone. One per day per deck. */
  date: string;
  /** Count of cards in Leitner box ≥ 5 at snapshot time. */
  masteredCount: number;
  /** Estimated CEFR level at snapshot time. */
  userLevel: CefrLevel;
};

type ProgressStore = Record<string, ProgressSnapshot[]>;

/** Keep ~5 weeks of history so the 30-day window always has headroom
 *  and we don't grow unbounded if the app runs for years. */
const PROGRESS_SNAPSHOT_CAP = 40;

function todayLocal(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function readStore(ls: Storage): ProgressStore {
  try {
    const raw = ls.getItem(STORAGE_KEYS.PROGRESS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as ProgressStore;
  } catch {
    return {};
  }
}

function writeStore(store: ProgressStore, ls: Storage): void {
  ls.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify(store));
}

/** Append today's snapshot if the latest entry isn't already today.
 *  Idempotent: calling multiple times in the same local day is a
 *  no-op. Trims the slice to the cap. */
export function recordProgressSnapshot(
  listId: string,
  snap: Omit<ProgressSnapshot, "date">,
  ls: Storage = localStorage,
  now: Date = new Date(),
): void {
  const date = todayLocal(now);
  const store = readStore(ls);
  const slice = store[listId] ?? [];
  const last = slice[slice.length - 1];
  if (last && last.date === date) return;
  const next = [...slice, { date, ...snap }];
  // Trim oldest entries beyond the cap. Keeps the tail (most recent).
  const trimmed =
    next.length > PROGRESS_SNAPSHOT_CAP
      ? next.slice(next.length - PROGRESS_SNAPSHOT_CAP)
      : next;
  store[listId] = trimmed;
  writeStore(store, ls);
}

/** Return the full per-deck snapshot history, oldest to newest. */
export function loadProgressHistory(
  listId: string,
  ls: Storage = localStorage,
): ProgressSnapshot[] {
  const store = readStore(ls);
  const slice = store[listId];
  return Array.isArray(slice) ? slice : [];
}

/** Find the snapshot closest to (but not after) the target date —
 *  used to compute "X cards mastered since N days ago". Returns null
 *  when the history doesn't go back that far. */
export function findSnapshotOnOrBefore(
  history: ProgressSnapshot[],
  targetDate: string,
): ProgressSnapshot | null {
  for (let i = history.length - 1; i >= 0; i--) {
    const snap = history[i]!;
    if (snap.date <= targetDate) return snap;
  }
  return null;
}

/** Date string N days before `now` (inclusive of today = 0). */
export function dateNDaysAgo(n: number, now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() - n);
  return todayLocal(d);
}

/** Oldest snapshot in the history, or null when empty. Used to decide
 *  whether a given trailing-window delta (7d / 30d) is displayable at
 *  all — e.g. don't show "30 days" when the user has 3 days of data. */
export function oldestSnapshotDate(
  history: ProgressSnapshot[],
): string | null {
  return history[0]?.date ?? null;
}
